import React, { useEffect, useRef, useState } from 'react';

/**
 * Modern searchable dropdown.
 * options: [{ value, label, meta? }]
 */
const SearchableSelect = ({
  label,
  value,
  displayLabel,
  options = [],
  onChange,
  placeholder = 'Search…',
  required = false,
  disabled = false,
  loading = false,
  onSearch,
  emptyMessage = 'No matches',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    const onDocClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = onSearch
    ? options
    : options.filter((opt) =>
        String(opt.label || '')
          .toLowerCase()
          .includes(query.toLowerCase())
      );

  const selectedLabel =
    displayLabel ||
    options.find((opt) => String(opt.value) === String(value))?.label ||
    '';

  const handleInputChange = (event) => {
    const next = event.target.value;
    setQuery(next);
    setOpen(true);
    if (onSearch) onSearch(next);
  };

  const handleSelect = (opt) => {
    onChange(opt);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className={`sv-select ${required ? 'required' : ''}`} ref={rootRef}>
      {label && (
        <label className="sv-select-label">
          {label}
          {required ? ' *' : ''}
        </label>
      )}
      <div className={`sv-select-control ${open ? 'open' : ''} ${disabled ? 'disabled' : ''}`}>
        <input
          type="text"
          className="sv-select-input"
          disabled={disabled}
          placeholder={selectedLabel || placeholder}
          value={open ? query : selectedLabel}
          onFocus={() => {
            if (!disabled) {
              setOpen(true);
              setQuery('');
              if (onSearch) onSearch('');
            }
          }}
          onChange={handleInputChange}
          autoComplete="off"
        />
        <span className="sv-select-chevron" aria-hidden="true" />
      </div>
      {open && !disabled && (
        <ul className="sv-select-menu">
          {loading && <li className="sv-select-empty">Loading…</li>}
          {!loading && filtered.length === 0 && (
            <li className="sv-select-empty">{emptyMessage}</li>
          )}
          {!loading &&
            filtered.map((opt) => (
              <li key={String(opt.value)}>
                <button
                  type="button"
                  className={`sv-select-option ${
                    String(opt.value) === String(value) ? 'selected' : ''
                  }`}
                  onClick={() => handleSelect(opt)}
                >
                  <span className="sv-select-option-label">{opt.label}</span>
                  {opt.meta ? <span className="sv-select-option-meta">{opt.meta}</span> : null}
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};

export default SearchableSelect;
